<template>
  <div class="page-container licenses-page">
    <portal to="page-name">Licenças</portal>

    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Gestão de Licenças</h1>
      </div>

      <div class="card-body">
        <div class="loading loading-lg mt-2" v-if="loading" />
        <template v-else>
          <div class="stats">
            <div class="stat-item">
              <div class="stat-label">Qtde. Licenças Contratadas</div>
              <div class="stat-value">{{ contract.totalLicenses }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-label">Qtde. Usuários Online</div>
              <div class="stat-value">{{ stats.licensesInUse }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-label">Qtde. Licenças Disponíveis</div>
              <div class="stat-value">
                {{ stats.availableLicenses }}
              </div>
            </div>
          </div>

          <div class="divider" />

          <div class="scheduled-reduction" v-if="scheduled">
            <fa-icon :icon="['fal', 'calendar-alt']" />
            <span>
              Redução para <strong>{{ scheduled.licenses }}</strong>
              {{ scheduled.licenses === 1 ? 'licença' : 'licenças' }} agendada para
              <strong>{{ scheduled.effectiveOn | date }}</strong>.
              Até lá a quantidade atual continua valendo.
            </span>
            <button
              type="button"
              class="btn btn-sm btn-link"
              :class="{ loading: cancelling }"
              :disabled="cancelling"
              @click="cancelScheduled"
            >
              Cancelar redução
            </button>
          </div>

          <div class="license-adjustment">
            <h6>Gerenciar Licenças</h6>

            <st-input-number-step
              class="mb-2"
              v-model="contract.newQuantity"
              :min-value="stats.licensesInUse"
              :readonly="saving"
            />

            <button
              class="btn btn-icon btn-icon-left btn-primary btn-block"
              @click="openConfirmationModal"
              :disabled="contract.newQuantity === contract.totalLicenses"
            >
              <fa-icon :icon="['fal', 'info-circle']" />
              Verificar nova mensalidade
            </button>
          </div>

          <div class="divider" />

          <div class="peak-section">
            <div class="peak-header">
              <div>
                <h6>Pico diário de acessos simultâneos</h6>
                <div>Últimos {{ chartPeak.data.labels.length }} dias</div>
              </div>

              <div class="btn-group" v-if="peakBranches.length > 1">
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="peakMode === 'customer' ? 'btn-primary' : 'btn-neutral'"
                  @click="peakMode = 'customer'"
                >
                  Total
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="peakMode === 'branch' ? 'btn-primary' : 'btn-neutral'"
                  @click="peakMode = 'branch'"
                >
                  Por unidade
                </button>
              </div>
            </div>

            <st-bar-chart
              :key="peakMode"
              :chart-data="chartPeak.data"
              :options="chartPeak.options"
              :height="245"
            />
          </div>

          <div class="divider" />

          <h6>Usuários Online</h6>
          <template v-if="userSessions.length === 0">
            <div class="empty">
              <div class="empty-icon">
                <fa-icon :icon="['fal', 'users']" size="3x"/>
              </div>
              <p class="empty-title h5">Nenhum usuário ativo</p>
              <p class="empty-subtitle">
                Não há usuários conectados no momento.
              </p>
            </div>
          </template>
          <table class="table table-striped table-hover" v-else>
            <thead>
            <tr>
              <th class="hide-sm" style="width:30px">#</th>
              <th style="min-width: 7rem">Última atividade</th>
              <th style="min-width: 10rem">Nome do usuário</th>
              <th style="min-width: 10rem">Dispositivo</th>
              <th/>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(session, i) in userSessions" :key="i">
              <td class="hide-sm">{{ i + 1 }}</td>
              <td>{{ session.lastSeenAt | date('DD/MM/YYYY HH:mm') }}</td>
              <td>{{ session.user.name }}</td>
              <td>
                {{ session.device }}
                <div class="text-small text-gray" v-if="session.ip">{{ session.ip }}</div>
                <div
                  class="text-small text-gray"
                  v-if="session.branches && session.branches.length > 1"
                >Unidades: {{ session.branches.map(({ name }) => name).join(', ') }}</div>
              </td>
              <td class="text-right">
                <button
                  class="btn btn-sm btn-action btn-icon btn-error"
                  :class="{loading: deletingUser === session.id}"
                  :disabled="deletingUser !== null"
                  @click="disconnectUser(session)"
                ><fa-icon :icon="['fal', 'sign-out-alt']" /></button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
    </div>

    <st-license-confirmation-modal
      :show="confirmationModalVisible"
      :contract="contract"
      :saving="saving"
      :stats="stats"
      @close="confirmationModalVisible = false"
      @confirm="saveLicenseChanges"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';

// Ordem fixa de cores por unidade: validada para daltonismo sobre a superfície clara.
// A cor acompanha a unidade, não a posição dela no gráfico.
const PEAK_COLORS = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100',
  '#e87ba4', '#008300', '#4a3aa7', '#e34948',
];
const PEAK_OTHER_COLOR = '#8a8a8a';

export default {
  data() {
    return {
      loading: false,
      saving: false,
      cancelling: false,
      deletingUser: null,
      confirmationModalVisible: false,
      stats: {
        licensesInUse: 0,
        availableLicenses: 0,
      },
      scheduled: null,
      contract: {
        activeSince: null,
        unitValue: 0,
        totalValue: 0,
        discountPercentage: 0,
        newQuantity: 0,
      },
      userSessions: [],
      peaks: [],
      peakBranches: [],
      peakMode: 'customer',
    };
  },
  computed: {
    ...mapGetters({
      isLicenseExceeded: 'isLicenseExceeded',
    }),
    // Unidades ordenadas pela participação no período. As que passam do número de cores
    // disponíveis entram juntas em "Outras", para o gráfico não virar um arco-íris.
    peakSeries() {
      const totals = new Map();

      this.peaks.forEach(({ branches }) => {
        (branches || []).forEach(({ id, peak }) => {
          const key = String(id);
          totals.set(key, (totals.get(key) || 0) + peak);
        });
      });

      return this.peakBranches
        .map(branch => ({
          key: String(branch.id),
          name: branch.name || 'Unidade sem nome',
          total: totals.get(String(branch.id)) || 0,
        }))
        .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'pt-BR'));
    },
    chartPeak() {
      const byBranch = this.peakMode === 'branch' && this.peakSeries.length > 0;

      return {
        data: {
          labels: this.peaks.map(item => moment(item.date).format('DD/MM/YY')),
          datasets: byBranch ? this.branchDatasets() : [{
            label: 'Acessos simultâneos',
            data: this.peaks.map(item => item.peak),
            backgroundColor: 'rgba(0,170,204,0.5)',
            borderColor: 'rgba(0,170,204,1)',
            borderWidth: 1,
          }],
        },
        options: {
          maintainAspectRatio: false,
          // Uma série só se identifica pelo título da seção; várias precisam da legenda.
          legend: {
            display: byBranch,
            position: 'bottom',
          },
          tooltips: byBranch
            ? { mode: 'index', intersect: false }
            : {},
          scales: {
            xAxes: [{
              stacked: byBranch,
              scaleLabel: {
                display: true,
                labelString: 'Data',
              },
            }],
            yAxes: [{
              stacked: byBranch,
              scaleLabel: {
                display: true,
                labelString: 'Acessos simultâneos',
              },
              ticks: {
                beginAtZero: true,
                precision: 0,
              },
            }],
          },
        },
      };
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await this.$http.get('/customer/licenses');
        this.stats = data.stats;
        this.scheduled = data.contract.scheduledLicenses;
        this.contract = {
          totalLicenses: data.contract.totalLicenses,
          activeSince: data.contract.activeSince,
          unitValue: data.contract.unitValue,
          totalValue: data.contract.totalValue,
          discountPercentage: data.contract.discountPercentage,
          newQuantity: data.contract.totalLicenses,
        };
        this.userSessions = data.userSessions;
        this.peaks = data.sessionPeaks || [];
        this.peakBranches = data.peakBranches || [];
        if (this.peakBranches.length < 2) this.peakMode = 'customer';
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
    },
    // Barras empilhadas: a altura total de cada dia continua sendo o número do modo
    // "Total", e cada fatia mostra de qual unidade ele veio.
    branchDatasets() {
      const shown = this.peakSeries.slice(0, PEAK_COLORS.length);
      const rest = this.peakSeries.slice(PEAK_COLORS.length);

      const valueFor = (item, keys) => (item.branches || [])
        .filter(({ id }) => keys.includes(String(id)))
        .reduce((sum, { peak }) => sum + peak, 0);

      const datasets = shown.map((branch, index) => ({
        label: branch.name,
        data: this.peaks.map(item => valueFor(item, [branch.key])),
        backgroundColor: PEAK_COLORS[index],
        borderColor: '#fff',
        borderWidth: 1,
      }));

      if (rest.length > 0) {
        const keys = rest.map(({ key }) => key);
        datasets.push({
          label: `Outras (${rest.length})`,
          data: this.peaks.map(item => valueFor(item, keys)),
          backgroundColor: PEAK_OTHER_COLOR,
          borderColor: '#fff',
          borderWidth: 1,
        });
      }

      return datasets;
    },
    async cancelScheduled() {
      try {
        this.cancelling = true;
        await this.$http.delete('/customer/licenses/scheduled');
        await this.load();
        this.$toast.success('Redução cancelada.');
      } catch (e) {
        this.$toast.error(e);
      }
      this.cancelling = false;
    },
    openConfirmationModal() {
      if (this.contract.newQuantity === this.contract.totalLicenses) {
        this.$toast.show('A quantidade não foi alterada.', { type: 'info' });
        return;
      }
      this.confirmationModalVisible = true;
    },
    async saveLicenseChanges(data) {
      try {
        this.saving = true;

        await this.$http.put('/customer/licenses', {
          licenses: this.contract.newQuantity,
        });

        const difference = this.contract.newQuantity - this.contract.totalLicenses;
        const count = Math.abs(difference);

        this.confirmationModalVisible = false;

        if (difference < 0) {
          await this.load();
          this.$toast.success(`Redução de ${count} licença(s) agendada para o próximo ciclo.`);
          return;
        }

        this.contract.totalLicenses = this.contract.newQuantity;
        this.contract.discountPercentage = data.newTier.percentage;
        this.contract.totalValue = data.newValue;

        this.$toast.success(`${count} licença(s) adicionada(s) com sucesso!`);

        if (this.isLicenseExceeded) {
          this.stats.availableLicenses = Math.max(
            0,
            this.contract.totalLicenses - this.stats.licensesInUse,
          );
          await this.$store.dispatch('loadUser');
        }
      } catch (e) {
        if (e?.response?.data?.message) {
          this.$toast.error(e.response.data.message);
          return;
        }
        this.$toast.error('Ocorreu um erro ao salvar as alterações. Tente novamente!');
      } finally {
        this.saving = false;
      }
    },
    async disconnectUser(session) {
      try {
        this.deletingUser = session.id;
        await this.$http.delete(`/sessions/${session.id}`);

        const index = this.userSessions.findIndex(({ id }) => id === session.id);
        if (index > -1) {
          this.userSessions.splice(index, 1);
          this.stats.licensesInUse -= 1;
          this.stats.availableLicenses += 1;
        }

        this.$toast.success(`Usuário ${session.user.name} desconectado com sucesso!`);

        if (this.isLicenseExceeded) {
          await this.$store.dispatch('loadUser');
        }
      } catch (e) {
        this.$toast.error('Ocorreu um erro ao desconectar o usuário. Tente novamente!');
      } finally {
        this.deletingUser = null;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.licenses-page {
  .peak-header {
    align-items: flex-start;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing;
    justify-content: space-between;
    margin-bottom: $layout-spacing;
  }

  .scheduled-reduction {
    align-items: center;
    background: $gray-color-light;
    border-left: $unit-1 solid $warning-color;
    display: flex;
    gap: $layout-spacing;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing $layout-spacing-lg;
  }

  .stats {
    display: grid;
    grid-column-gap: $layout-spacing;
    grid-row-gap: $layout-spacing;
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: $layout-spacing-lg;
    .stat-item {
      background-color: $gray-color-ultra-light;
      border: $border-width solid $border-color;
      border-radius: $border-radius;
      padding: $layout-spacing $layout-spacing-lg;
    }
    .stat-value {
      color: $primary-color;
      font-size: $unit-5;
      font-weight: 600;
    }
  }

  .divider {
    margin: $layout-spacing-xl 0;
  }

  .license-adjustment {
    margin: 0 auto;
    max-width: 12rem;
    .btn.btn-primary {
      height: 2rem;
    }
  }
}
</style>
